import React from "react";

import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme
} from "@material-ui/core";

import CreateIcon from "@material-ui/icons/Create";
import InboxIcon from "@material-ui/icons/Inbox";
import SendIcon from "@material-ui/icons/Send";
import SearchIcon from "@material-ui/icons/Search";
import RefreshIcon from "@material-ui/icons/Refresh";
import MailOutlineIcon from "@material-ui/icons/MailOutline";
import AttachFileIcon from "@material-ui/icons/AttachFile";
import SettingsOutlinedIcon from "@material-ui/icons/SettingsOutlined";
import AddIcon from "@material-ui/icons/Add";
import FolderOutlinedIcon from "@material-ui/icons/FolderOutlined";
import EditOutlinedIcon from "@material-ui/icons/EditOutlined";

import api from "../../../services/api";
import toastError from "../../../errors/toastError";


import ArchiveOutlinedIcon from "@material-ui/icons/ArchiveOutlined";
import ReportProblemOutlinedIcon from "@material-ui/icons/ReportProblemOutlined";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";

const safeArray = value =>
  Array.isArray(value)
    ? value
    : [];


const personName = value => {

  if (!value) {
    return "";
  }

  if (Array.isArray(value)) {

    return value
      .map(personName)
      .filter(Boolean)
      .join(", ");
  }

  if (typeof value === "string") {
    return value;
  }

  return (
    value.name ||
    value.address ||
    value.email ||
    ""
  );
};


const personAddress = value => {

  if (!value) {
    return "";
  }

  if (Array.isArray(value)) {

    return value
      .map(personAddress)
      .filter(Boolean)
      .join(", ");
  }

  if (typeof value === "string") {
    return value;
  }

  return (
    value.address ||
    value.email ||
    value.name ||
    ""
  );
};


const initials = value => {

  const clean =
    String(
      value || "?"
    ).trim();

  if (!clean) {
    return "?";
  }

  const source =
    clean.includes("@")
      ? clean.split("@")[0]
      : clean;

  return source
    .split(/[\s._-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(
      item =>
        item
          .charAt(0)
          .toUpperCase()
    )
    .join("");
};


const formatEmailDate = value => {

  if (!value) {
    return "";
  }

  const date =
    new Date(value);

  if (
    Number.isNaN(
      date.getTime()
    )
  ) {
    return "";
  }

  const now =
    new Date();

  const sameDay =
    date.getDate() ===
      now.getDate() &&
    date.getMonth() ===
      now.getMonth() &&
    date.getFullYear() ===
      now.getFullYear();

  if (sameDay) {

    return new Intl.DateTimeFormat(
      "pt-BR",
      {
        hour: "2-digit",
        minute: "2-digit"
      }
    ).format(date);
  }

  if (
    date.getFullYear() ===
    now.getFullYear()
  ) {

    return new Intl.DateTimeFormat(
      "pt-BR",
      {
        day: "2-digit",
        month: "short"
      }
    )
      .format(date)
      .replace(".", "");
  }

  return new Intl.DateTimeFormat(
    "pt-BR",
    {
      day: "2-digit",
      month: "2-digit",
      year: "2-digit"
    }
  ).format(date);
};


const folderButtonStyle = active => ({
  minHeight: 42,
  borderRadius: 10,
  justifyContent: "flex-start",
  paddingLeft: 13,
  paddingRight: 13,
  textTransform: "none",
  fontSize: 12,
  fontWeight: active
    ? 800
    : 650,
  color: active
    ? "#1849A9"
    : "#475467",
  background: active
    ? "#EEF4FF"
    : "transparent"
});


const EmailWorkspaceV2 = ({
  user,

  companies,
  selectedCompanyId,
  onCompanyChange,

  connected,
  mailboxes,

  messages,
  filteredMessages,

  mailFolder,
  setMailFolder,

  mailboxFilter,
  setMailboxFilter,

  unreadOnly,
  setUnreadOnly,

  attachmentOnly,
  setAttachmentOnly,

  dateFilter,
  setDateFilter,

  search,
  setSearch,

  loading,
  inboxLoading,

  onRefresh,
  onOpenMessage,
  onToggleFavorite,
  onNewEmail,
  onConfigure,

  accessManager
}) => {

  const theme =
    useTheme();

  const isMobile =
    useMediaQuery(
      theme.breakpoints.down("sm")
    );


  // KORAX EMAIL SMART FOLDERS V1

  const [
    smartFolders,
    setSmartFolders
  ] = React.useState([]);


  const [
    activeSmartFolderId,
    setActiveSmartFolderId
  ] = React.useState("");


  const canManageSmartFolders =
    Boolean(
      user?.super
    ) ||
    String(
      user?.profile ||
      ""
    )
      .toLowerCase() ===
      "admin";


  // KORAX SMART FOLDER MODAL V2

  const [
    smartFolderModalOpen,
    setSmartFolderModalOpen
  ] = React.useState(false);


  const [
    smartFolderEditing,
    setSmartFolderEditing
  ] = React.useState(null);


  const [
    smartFolderName,
    setSmartFolderName
  ] = React.useState("");


  const [
    smartFolderRules,
    setSmartFolderRules
  ] = React.useState("");


  const [
    smartFolderError,
    setSmartFolderError
  ] = React.useState("");


  const [
    smartFolderSaving,
    setSmartFolderSaving
  ] = React.useState(false);


  const [
    smartFolderDeleteTarget,
    setSmartFolderDeleteTarget
  ] = React.useState(null);


  const [
    smartFolderDeleting,
    setSmartFolderDeleting
  ] = React.useState(false);


  const parseSmartRules =
    value =>
      String(
        value || ""
      )
        .split(
          /[\n,;]+/
        )
        .map(
          item =>
            item
              .trim()
              .toLowerCase()
        )
        .filter(Boolean);


  const messageMatchesSmartFolder =
    (
      message,
      folder
    ) => {

      if (!folder) {
        return false;
      }


      const sender =
        String(
          personAddress(
            message?.from
          ) ||
          ""
        )
          .trim()
          .toLowerCase();


      if (!sender) {
        return false;
      }


      return safeArray(
        folder?.senders
      ).some(
        rule => {

          const cleanRule =
            String(
              rule || ""
            )
              .trim()
              .toLowerCase();


          if (!cleanRule) {
            return false;
          }


          if (
            cleanRule
              .startsWith("@")
          ) {

            return sender
              .endsWith(
                cleanRule
              );
          }


          return sender ===
            cleanRule;
        }
      );
    };


  const loadSmartFolders =
    async () => {

      if (
        !selectedCompanyId
      ) {

        setSmartFolders(
          []
        );

        return;
      }


      try {

        const {
          data
        } =
          await api.get(
            "/email-beta/smart-folders",
            {
              params: {
                companyId:
                  selectedCompanyId
              }
            }
          );


        setSmartFolders(
          safeArray(
            data?.folders
          )
        );

      } catch (err) {

        toastError(
          err
        );
      }
    };


  React.useEffect(
    () => {

      setActiveSmartFolderId(
        ""
      );

      loadSmartFolders();

      // eslint-disable-next-line
    },
    [
      selectedCompanyId
    ]
  );


  const selectedSmartFolder =
    smartFolders.find(
      folder =>
        String(
          folder?.id
        ) ===
        String(
          activeSmartFolderId
        )
    );


  const closeSmartFolderModal =
    () => {

      if (
        smartFolderSaving
      ) {
        return;
      }


      setSmartFolderModalOpen(
        false
      );

      setSmartFolderEditing(
        null
      );

      setSmartFolderName(
        ""
      );

      setSmartFolderRules(
        ""
      );

      setSmartFolderError(
        ""
      );
    };


  const handleOpenCreateSmartFolder =
    () => {

      if (
        !canManageSmartFolders ||
        !selectedCompanyId
      ) {
        return;
      }


      setSmartFolderEditing(
        null
      );

      setSmartFolderName(
        ""
      );

      setSmartFolderRules(
        ""
      );

      setSmartFolderError(
        ""
      );

      setSmartFolderModalOpen(
        true
      );
    };


  const handleOpenEditSmartFolder =
    folder => {

      if (
        !canManageSmartFolders ||
        !folder
      ) {
        return;
      }


      setSmartFolderEditing(
        folder
      );

      setSmartFolderName(
        folder.name ||
        ""
      );

      setSmartFolderRules(
        safeArray(
          folder.senders
        ).join(
          "\n"
        )
      );

      setSmartFolderError(
        ""
      );

      setSmartFolderModalOpen(
        true
      );
    };


  const handleSaveSmartFolder =
    async () => {

      if (
        !canManageSmartFolders ||
        !selectedCompanyId ||
        smartFolderSaving
      ) {
        return;
      }


      const name =
        String(
          smartFolderName ||
          ""
        ).trim();


      if (!name) {

        setSmartFolderError(
          "Informe um nome para a pasta."
        );

        return;
      }


      const senders =
        parseSmartRules(
          smartFolderRules
        );


      if (
        !senders.length
      ) {

        setSmartFolderError(
          "Informe pelo menos um remetente ou domínio."
        );

        return;
      }


      const invalid =
        senders.find(
          rule => {

            const email =
              /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(
                rule
              );

            const domain =
              /^@[^\s@]+\.[^\s@]+$/.test(
                rule
              );


            return (
              !email &&
              !domain
            );
          }
        );


      if (invalid) {

        setSmartFolderError(
          `Regra inválida: ${invalid}`
        );

        return;
      }


      setSmartFolderError(
        ""
      );

      setSmartFolderSaving(
        true
      );


      try {

        if (
          smartFolderEditing
            ?.id
        ) {

          await api.put(
            `/email-beta/smart-folders/${smartFolderEditing.id}`,
            {
              companyId:
                selectedCompanyId,

              name,

              senders
            }
          );

        } else {

          await api.post(
            "/email-beta/smart-folders",
            {
              companyId:
                selectedCompanyId,

              name,

              senders
            }
          );
        }


        await loadSmartFolders();


        setSmartFolderModalOpen(
          false
        );

        setSmartFolderEditing(
          null
        );

        setSmartFolderName(
          ""
        );

        setSmartFolderRules(
          ""
        );

        setSmartFolderError(
          ""
        );

      } catch (err) {

        toastError(
          err
        );

      } finally {

        setSmartFolderSaving(
          false
        );
      }
    };


  const handleRequestDeleteSmartFolder =
    folder => {

      if (
        !canManageSmartFolders ||
        !folder
      ) {
        return;
      }


      setSmartFolderDeleteTarget(
        folder
      );
    };


  const closeDeleteSmartFolder =
    () => {

      if (
        smartFolderDeleting
      ) {
        return;
      }


      setSmartFolderDeleteTarget(
        null
      );
    };


  const handleConfirmDeleteSmartFolder =
    async () => {

      const folder =
        smartFolderDeleteTarget;


      if (
        !folder ||
        !canManageSmartFolders ||
        smartFolderDeleting
      ) {
        return;
      }


      setSmartFolderDeleting(
        true
      );


      try {

        await api.delete(
          `/email-beta/smart-folders/${folder.id}`,
          {
            params: {
              companyId:
                selectedCompanyId
            }
          }
        );


        if (
          String(
            activeSmartFolderId
          ) ===
          String(
            folder.id
          )
        ) {

          setActiveSmartFolderId(
            ""
          );
        }


        setSmartFolderDeleteTarget(
          null
        );


        await loadSmartFolders();

      } catch (err) {

        toastError(
          err
        );

      } finally {

        setSmartFolderDeleting(
          false
        );
      }
    };


  const handleOpenSmartFolder =
    folder => {

      if (!folder) {
        return;
      }


      setMailboxFilter(
        "all"
      );

      setUnreadOnly(
        false
      );

      setFavoriteOnly(
        false
      );

      setAttachmentOnly(
        false
      );

      setDateFilter(
        "all"
      );

      setSearch(
        ""
      );

      setMailFolder(
        "inbox"
      );

      setActiveSmartFolderId(
        String(
          folder.id
        )
      );
    };


  const boxRows =
    safeArray(
      mailboxes
    );

  const allMessages =
    safeArray(
      messages
    );

  // KORAX EMAIL FAVORITES FILTER V1.3
  const [
    favoriteOnly,
    setFavoriteOnly
  ] = React.useState(false);


  const baseVisibleMessages =
    safeArray(
      filteredMessages
    );


  const smartFolderVisibleMessages =
    activeSmartFolderId &&
    mailFolder === "inbox"
      ? baseVisibleMessages.filter(
          message =>
            messageMatchesSmartFolder(
              message,
              selectedSmartFolder
            )
        )
      : baseVisibleMessages;


  const visibleMessages =
    favoriteOnly
      ? smartFolderVisibleMessages.filter(
          message =>
            safeArray(
              message?.flags
            ).some(
              flag =>
                String(flag) ===
                "\\Flagged"
            )
        )
      : smartFolderVisibleMessages;


  const ready =
    Boolean(
      connected &&
      boxRows.length > 0
    );


  const unreadCount =
    allMessages.filter(
      item =>
        Boolean(
          item?.unseen
        )
    ).length;


  const selectedCompany =
    safeArray(
      companies
    ).find(
      company =>
        String(
          company?.id
        ) ===
        String(
          selectedCompanyId
        )
    );


  const countForMailbox =
    email =>
      allMessages.filter(
        item =>
          String(
            item
              ?.mailboxAddress ||
            ""
          ) ===
          String(
            email || ""
          )
      ).length;


  const changeFolder =
    folder => {

      setMailboxFilter(
        "all"
      );

      setUnreadOnly(
        false
      );

      setFavoriteOnly(
        false
      );

      setAttachmentOnly(
        false
      );

      setDateFilter(
        "all"
      );

      setSearch(
        ""
      );

      setActiveSmartFolderId(
        ""
      );

      setMailFolder(
        folder
      );
    };


  // KORAX EMAIL FILTER TOOLBAR V1.2

  const filtersActive =
    Boolean(
      String(
        search ||
        ""
      ).trim() ||
      mailboxFilter !==
        "all" ||
      unreadOnly ||
      favoriteOnly ||
      attachmentOnly ||
      dateFilter !==
        "all"
    );


  const clearFilters =
    () => {

      setSearch(
        ""
      );

      setMailboxFilter(
        "all"
      );

      setUnreadOnly(
        false
      );

      setAttachmentOnly(
        false
      );

      setDateFilter(
        "all"
      );
    };


  const quickFilterStyle =
    active => ({
      minHeight: 30,
      minWidth: "auto",
      padding:
        "4px 10px",
      borderRadius: 8,
      border:
        active
          ? "1px solid #B2CCFF"
          : "1px solid #D0D5DD",
      background:
        active
          ? "#EEF4FF"
          : "#FFFFFF",
      color:
        active
          ? "#1849A9"
          : "#475467",
      textTransform:
        "none",
      fontSize: 10,
      fontWeight: 800,
      boxShadow: "none"
    });



  const folderLabel =
    selectedSmartFolder &&
    activeSmartFolderId
      ? selectedSmartFolder.name
      : ({
          inbox:
            "Entrada",

          sent:
            "Enviados",

          archive:
            "Arquivados",

          spam:
            "Spam",

          trash:
            "Lixeira"
        }[mailFolder] ||
        "Entrada");


  return (
    <Box
      style={{
        padding:
          isMobile
            ? 12
            : 24,
        maxWidth: 1480,
        margin: "0 auto"
      }}
    >

      {/* KORAX SMART FOLDER MODAL V2 */}

      <Dialog
        open={
          smartFolderModalOpen
        }
        onClose={
          closeSmartFolderModal
        }
        fullWidth
        maxWidth="sm"
        PaperProps={{
          style: {
            borderRadius: 18,
            overflow: "hidden"
          }
        }}
      >
        <DialogTitle
          disableTypography
          style={{
            padding:
              "22px 24px 18px"
          }}
        >
          <Typography
            style={{
              color: "#101828",
              fontSize: 19,
              fontWeight: 900,
              letterSpacing:
                "-.025em"
            }}
          >
            {smartFolderEditing
              ? "Editar pasta inteligente"
              : "Nova pasta inteligente"}
          </Typography>


          <Typography
            style={{
              marginTop: 5,
              color: "#667085",
              fontSize: 11.5,
              lineHeight: 1.55
            }}
          >
            Organize automaticamente os e-mails por remetente ou domínio.
          </Typography>
        </DialogTitle>


        <Divider />


        <DialogContent
          style={{
            padding:
              "22px 24px 24px"
          }}
        >
          <TextField
            fullWidth
            autoFocus
            variant="outlined"
            size="small"
            label="Nome da pasta"
            placeholder="Ex.: Serviços"
            value={
              smartFolderName
            }
            onChange={
              event => {
                setSmartFolderName(
                  event.target.value
                );

                setSmartFolderError(
                  ""
                );
              }
            }
            disabled={
              smartFolderSaving
            }
          />


          <Box
            style={{
              marginTop: 22
            }}
          >
            <Typography
              style={{
                color: "#344054",
                fontSize: 11,
                fontWeight: 850,
                marginBottom: 6
              }}
            >
              Regras de remetente
            </Typography>


            <Typography
              style={{
                color: "#667085",
                fontSize: 10,
                lineHeight: 1.55,
                marginBottom: 10
              }}
            >
              Digite um e-mail por linha. Você também pode usar um domínio inteiro.
            </Typography>


            <TextField
              fullWidth
              multiline
              rows={5}
              variant="outlined"
              placeholder={
                "paulo@servicos.com.br\nfinanceiro@servicos.com.br\n@fornecedor.com.br"
              }
              value={
                smartFolderRules
              }
              onChange={
                event => {
                  setSmartFolderRules(
                    event.target.value
                  );

                  setSmartFolderError(
                    ""
                  );
                }
              }
              disabled={
                smartFolderSaving
              }
            />


            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              style={{
                marginTop: 8,
                gap: 12
              }}
            >
              <Typography
                style={{
                  color: "#98A2B3",
                  fontSize: 9.5
                }}
              >
                Também aceita vírgula ou ponto e vírgula.
              </Typography>


              <Typography
                style={{
                  color: "#667085",
                  fontSize: 9.5,
                  fontWeight: 750
                }}
              >
                {parseSmartRules(
                  smartFolderRules
                ).length}{" "}
                {parseSmartRules(
                  smartFolderRules
                ).length === 1
                  ? "regra"
                  : "regras"}
              </Typography>
            </Box>
          </Box>


          {smartFolderError && (
            <Box
              style={{
                marginTop: 18,
                padding:
                  "10px 12px",
                borderRadius: 10,
                border:
                  "1px solid #FECDCA",
                background:
                  "#FEF3F2"
              }}
            >
              <Typography
                style={{
                  color: "#B42318",
                  fontSize: 10.5,
                  fontWeight: 700
                }}
              >
                {smartFolderError}
              </Typography>
            </Box>
          )}
        </DialogContent>


        <Divider />


        <DialogActions
          style={{
            padding:
              "15px 20px 17px",
            gap: 8
          }}
        >
          <Button
            onClick={
              closeSmartFolderModal
            }
            disabled={
              smartFolderSaving
            }
            style={{
              textTransform:
                "none",
              fontWeight: 750,
              color: "#475467"
            }}
          >
            Cancelar
          </Button>


          <Button
            variant="contained"
            onClick={
              handleSaveSmartFolder
            }
            disabled={
              smartFolderSaving
            }
            style={{
              minWidth: 130,
              borderRadius: 9,
              textTransform:
                "none",
              fontWeight: 800,
              background:
                "#0057FF",
              color: "#FFFFFF",
              boxShadow: "none"
            }}
          >
            {smartFolderSaving
              ? "Salvando..."
              : smartFolderEditing
                ? "Salvar alterações"
                : "Criar pasta"}
          </Button>
        </DialogActions>
      </Dialog>


      <Dialog
        open={
          Boolean(
            smartFolderDeleteTarget
          )
        }
        onClose={
          closeDeleteSmartFolder
        }
        fullWidth
        maxWidth="xs"
        PaperProps={{
          style: {
            borderRadius: 18,
            overflow: "hidden"
          }
        }}
      >
        <DialogTitle
          disableTypography
          style={{
            padding:
              "22px 24px 12px"
          }}
        >
          <Typography
            style={{
              color: "#101828",
              fontSize: 18,
              fontWeight: 900
            }}
          >
            Excluir pasta?
          </Typography>
        </DialogTitle>


        <DialogContent
          style={{
            padding:
              "8px 24px 22px"
          }}
        >
          <Typography
            style={{
              color: "#667085",
              fontSize: 11.5,
              lineHeight: 1.6
            }}
          >
            A pasta{" "}
            <strong>
              {smartFolderDeleteTarget
                ?.name ||
                ""}
            </strong>{" "}
            será removida.
          </Typography>


          <Box
            style={{
              marginTop: 14,
              padding:
                "10px 12px",
              borderRadius: 10,
              background:
                "#F9FAFB",
              border:
                "1px solid #EAECF0"
            }}
          >
            <Typography
              style={{
                color: "#475467",
                fontSize: 10.5,
                lineHeight: 1.55
              }}
            >
              Os e-mails originais continuarão normalmente na caixa de entrada.
            </Typography>
          </Box>
        </DialogContent>


        <Divider />


        <DialogActions
          style={{
            padding:
              "14px 20px 16px",
            gap: 8
          }}
        >
          <Button
            onClick={
              closeDeleteSmartFolder
            }
            disabled={
              smartFolderDeleting
            }
            style={{
              textTransform:
                "none",
              fontWeight: 750,
              color: "#475467"
            }}
          >
            Cancelar
          </Button>


          <Button
            variant="contained"
            onClick={
              handleConfirmDeleteSmartFolder
            }
            disabled={
              smartFolderDeleting
            }
            style={{
              borderRadius: 9,
              textTransform:
                "none",
              fontWeight: 800,
              background:
                "#D92D20",
              color: "#FFFFFF",
              boxShadow: "none"
            }}
          >
            {smartFolderDeleting
              ? "Excluindo..."
              : "Excluir pasta"}
          </Button>
        </DialogActions>
      </Dialog>


      {/* HEADER */}

      <Box
        display="flex"
        alignItems={
          isMobile
            ? "stretch"
            : "center"
        }
        justifyContent="space-between"
        style={{
          gap: 16,
          marginBottom: 18,
          flexDirection:
            isMobile
              ? "column"
              : "row"
        }}
      >
        <Box>
          <Typography
            style={{
              color: "#101828",
              fontSize:
                isMobile
                  ? 23
                  : 28,
              fontWeight: 900,
              letterSpacing: "-.035em"
            }}
          >
            E-mail
          </Typography>

          <Typography
            style={{
              marginTop: 4,
              color: "#667085",
              fontSize: 11.5
            }}
          >
            Sua central de e-mails, organizada em um só lugar.
          </Typography>
        </Box>


        <Box
          display="flex"
          alignItems="center"
          style={{
            gap: 8,
            flexWrap: "wrap"
          }}
        >
          {user?.super &&
            safeArray(
              companies
            ).length > 1 && (
              <TextField
                select
                variant="outlined"
                size="small"
                value={
                  selectedCompanyId ||
                  ""
                }
                onChange={
                  onCompanyChange
                }
                style={{
                  width:
                    isMobile
                      ? "100%"
                      : 210
                }}
              >
                {safeArray(
                  companies
                ).map(
                  company => (
                    <MenuItem
                      key={
                        company.id
                      }
                      value={
                        String(
                          company.id
                        )
                      }
                    >
                      {company.name}
                    </MenuItem>
                  )
                )}
              </TextField>
            )}


          {accessManager}


          {user?.super && (
            <Button
              variant="outlined"
              startIcon={
                <SettingsOutlinedIcon />
              }
              onClick={
                onConfigure
              }
              style={{
                height: 40,
                borderRadius: 10,
                borderColor: "#D0D5DD",
                color: "#344054",
                background: "#FFFFFF",
                textTransform: "none",
                fontSize: 11,
                fontWeight: 750
              }}
            >
              Configurar caixas
            </Button>
          )}
        </Box>
      </Box>


      {/* STATUS SUTIL */}

      {ready && (
        <Box
          display="flex"
          alignItems="center"
          style={{
            gap: 7,
            marginBottom: 12
          }}
        >
          <Box
            style={{
              width: 7,
              height: 7,
              borderRadius: "50%",
              background: "#12B76A"
            }}
          />

          <Typography
            style={{
              color: "#667085",
              fontSize: 10.5,
              fontWeight: 650
            }}
          >
            {boxRows.length === 1
              ? "1 caixa disponível"
              : `${boxRows.length} caixas disponíveis`}
            {selectedCompany?.name
              ? ` • ${selectedCompany.name}`
              : ""}
          </Typography>
        </Box>
      )}


      <Paper
        elevation={0}
        style={{
          minHeight:
            isMobile
              ? 640
              : 690,
          display:
            isMobile
              ? "block"
              : "grid",
          gridTemplateColumns:
            "258px minmax(0, 1fr)",
          overflow: "hidden",
          border:
            "1px solid #E4E7EC",
          borderRadius: 18,
          background: "#FFFFFF",
          boxShadow:
            "0 4px 18px rgba(16,24,40,.035)"
        }}
      >

        {/* SIDEBAR */}

        <Box
          style={{
            padding: 14,
            borderRight:
              isMobile
                ? "none"
                : "1px solid #EAECF0",
            borderBottom:
              isMobile
                ? "1px solid #EAECF0"
                : "none",
            background: "#FCFCFD"
          }}
        >
          <Button
            fullWidth
            onClick={
              onNewEmail
            }
            startIcon={
              <CreateIcon />
            }
            style={{
              minHeight: 44,
              marginBottom: 13,
              justifyContent:
                "flex-start",
              paddingLeft: 15,
              borderRadius: 11,
              color: "#FFFFFF",
              background:
                "linear-gradient(135deg, #010B36 0%, #0057FF 100%)",
              textTransform: "none",
              fontSize: 12,
              fontWeight: 850,
              boxShadow:
                "0 7px 18px rgba(0,87,255,.18)"
            }}
          >
            Novo e-mail
          </Button>


          <Button
            fullWidth
            startIcon={
              <InboxIcon />
            }
            onClick={
              () =>
                changeFolder(
                  "inbox"
                )
            }
            style={
              folderButtonStyle(
                mailFolder ===
                "inbox"
              )
            }
          >
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              style={{
                width: "100%"
              }}
            >
              <span>Entrada</span>

              {unreadCount > 0 &&
                mailFolder ===
                  "inbox" && (
                  <Box
                    style={{
                      minWidth: 22,
                      height: 20,
                      padding:
                        "0 6px",
                      borderRadius: 10,
                      display: "flex",
                      alignItems:
                        "center",
                      justifyContent:
                        "center",
                      background:
                        "#D1E9FF",
                      color:
                        "#175CD3",
                      fontSize: 9,
                      fontWeight: 850
                    }}
                  >
                    {unreadCount}
                  </Box>
                )}
            </Box>
          </Button>


          <Button
            fullWidth
            startIcon={
              <SendIcon />
            }
            onClick={
              () =>
                changeFolder(
                  "sent"
                )
            }
            style={{
              ...folderButtonStyle(
                mailFolder ===
                "sent"
              ),
              marginTop: 3
            }}
          >
            Enviados
          </Button>

          {/* KORAX EMAIL FOLDER SIDEBAR V1 */}

          <Button
            fullWidth
            startIcon={<ArchiveOutlinedIcon />}
            onClick={() =>
              changeFolder("archive")
            }
            style={{
              ...folderButtonStyle(
                mailFolder === "archive"
              ),
              marginTop: 3
            }}
          >
            Arquivados
          </Button>

          <Button
            fullWidth
            startIcon={
              <ReportProblemOutlinedIcon />
            }
            onClick={() =>
              changeFolder("spam")
            }
            style={{
              ...folderButtonStyle(
                mailFolder === "spam"
              ),
              marginTop: 3
            }}
          >
            Spam
          </Button>

          <Button
            fullWidth
            startIcon={<DeleteOutlineIcon />}
            onClick={() =>
              changeFolder("trash")
            }
            style={{
              ...folderButtonStyle(
                mailFolder === "trash"
              ),
              marginTop: 3
            }}
          >
            Lixeira
          </Button>



          {/* KORAX EMAIL SMART FOLDERS V1 */}

          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            style={{
              marginTop: 20,
              marginBottom: 7,
              padding:
                "0 8px 0 10px"
            }}
          >
            <Typography
              style={{
                color:
                  "#98A2B3",
                fontSize: 9,
                fontWeight: 850,
                letterSpacing:
                  ".075em",
                textTransform:
                  "uppercase"
              }}
            >
              Pastas inteligentes
            </Typography>


            {canManageSmartFolders && (
              <Tooltip
                title="Nova pasta inteligente"
              >
                <IconButton
                  size="small"
                  onClick={
                    handleOpenCreateSmartFolder
                  }
                  style={{
                    padding: 4
                  }}
                >
                  <AddIcon
                    style={{
                      fontSize: 17
                    }}
                  />
                </IconButton>
              </Tooltip>
            )}
          </Box>


          {smartFolders.length ===
          0 ? (
            <Typography
              style={{
                padding:
                  "5px 10px 8px",
                color:
                  "#98A2B3",
                fontSize: 9.5,
                lineHeight: 1.45
              }}
            >
              Nenhuma pasta criada.
            </Typography>
          ) : (
            <Box
              style={{
                display: "grid",
                gap: 3
              }}
            >
              {smartFolders.map(
                folder => {

                  const active =
                    String(
                      activeSmartFolderId
                    ) ===
                    String(
                      folder.id
                    );


                  return (
                    <Box
                      key={
                        folder.id
                      }
                      display="flex"
                      alignItems="center"
                      style={{
                        gap: 3
                      }}
                    >
                      <Button
                        fullWidth
                        startIcon={
                          <FolderOutlinedIcon />
                        }
                        onClick={
                          () =>
                            handleOpenSmartFolder(
                              folder
                            )
                        }
                        style={{
                          ...folderButtonStyle(
                            active
                          ),
                          minWidth: 0
                        }}
                      >
                        <Typography
                          noWrap
                          style={{
                            fontSize: 10.5,
                            fontWeight:
                              active
                                ? 800
                                : 700,
                            textAlign:
                              "left",
                            width: "100%"
                          }}
                        >
                          {folder.name}
                        </Typography>
                      </Button>


                      {canManageSmartFolders && (
                        <>
                          <Tooltip
                            title="Editar pasta"
                          >
                            <IconButton
                              size="small"
                              onClick={
                                () =>
                                  handleOpenEditSmartFolder(
                                    folder
                                  )
                              }
                              style={{
                                padding: 5
                              }}
                            >
                              <EditOutlinedIcon
                                style={{
                                  fontSize: 15
                                }}
                              />
                            </IconButton>
                          </Tooltip>


                          <Tooltip
                            title="Excluir pasta"
                          >
                            <IconButton
                              size="small"
                              onClick={
                                () =>
                                  handleRequestDeleteSmartFolder(
                                    folder
                                  )
                              }
                              style={{
                                padding: 5
                              }}
                            >
                              <DeleteOutlineIcon
                                style={{
                                  fontSize: 16
                                }}
                              />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </Box>
                  );
                }
              )}
            </Box>
          )}


          <Box
            style={{
              marginTop: 22,
              marginBottom: 8,
              padding: "0 10px"
            }}
          >
            <Typography
              style={{
                color: "#98A2B3",
                fontSize: 9,
                fontWeight: 850,
                letterSpacing: ".075em",
                textTransform: "uppercase"
              }}
            >
              Minhas caixas
            </Typography>
          </Box>


          <Box
            style={{
              display: "grid",
              gap: 3,
              maxHeight:
                isMobile
                  ? 160
                  : 330,
              overflowY: "auto"
            }}
          >
            <Button
              fullWidth
              onClick={() =>
                setMailboxFilter(
                  "all"
                )
              }
              style={{
                minHeight: 38,
                borderRadius: 9,
                justifyContent:
                  "flex-start",
                padding:
                  "7px 10px",
                textTransform:
                  "none",
                background:
                  mailboxFilter ===
                    "all"
                    ? "#F2F4F7"
                    : "transparent",
                color:
                  mailboxFilter ===
                    "all"
                    ? "#101828"
                    : "#667085"
              }}
            >
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                style={{
                  width: "100%",
                  minWidth: 0
                }}
              >
                <Box
                  display="flex"
                  alignItems="center"
                  style={{
                    minWidth: 0,
                    gap: 8
                  }}
                >
                  <MailOutlineIcon
                    style={{
                      fontSize: 17
                    }}
                  />

                  <Typography
                    noWrap
                    style={{
                      fontSize: 10.5,
                      fontWeight: 700
                    }}
                  >
                    Todas as caixas
                  </Typography>
                </Box>

                <Typography
                  style={{
                    marginLeft: 5,
                    color: "#98A2B3",
                    fontSize: 9,
                    fontWeight: 750
                  }}
                >
                  {allMessages.length}
                </Typography>
              </Box>
            </Button>


            {boxRows.map(
              mailbox => {

                const active =
                  String(
                    mailboxFilter
                  ) ===
                  String(
                    mailbox?.email
                  );

                return (
                  <Button
                    key={
                      mailbox.id ||
                      mailbox.email
                    }
                    fullWidth
                    onClick={() =>
                      setMailboxFilter(
                        mailbox.email
                      )
                    }
                    style={{
                      minHeight: 40,
                      borderRadius: 9,
                      justifyContent:
                        "flex-start",
                      padding:
                        "7px 10px",
                      textTransform:
                        "none",
                      background:
                        active
                          ? "#EEF4FF"
                          : "transparent",
                      color:
                        active
                          ? "#1849A9"
                          : "#667085"
                    }}
                  >
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                      style={{
                        width: "100%",
                        minWidth: 0
                      }}
                    >
                      <Box
                        display="flex"
                        alignItems="center"
                        style={{
                          minWidth: 0,
                          gap: 8
                        }}
                      >
                        <Box
                          style={{
                            width: 8,
                            height: 8,
                            flexShrink: 0,
                            borderRadius:
                              "50%",
                            background:
                              active
                                ? "#0057FF"
                                : "#D0D5DD"
                          }}
                        />

                        <Typography
                          noWrap
                          style={{
                            fontSize: 9.7,
                            fontWeight:
                              active
                                ? 800
                                : 650
                          }}
                        >
                          {mailbox.email}
                        </Typography>
                      </Box>

                      <Typography
                        style={{
                          marginLeft: 5,
                          color:
                            active
                              ? "#175CD3"
                              : "#98A2B3",
                          fontSize: 8.5,
                          fontWeight: 750
                        }}
                      >
                        {countForMailbox(
                          mailbox.email
                        )}
                      </Typography>
                    </Box>
                  </Button>
                );
              }
            )}
          </Box>
        </Box>


        {/* CONTEUDO */}

        <Box
          style={{
            minWidth: 0,
            background: "#FFFFFF"
          }}
        >

          {/* TOOLBAR */}

          <Box
            display="flex"
            alignItems={
              isMobile
                ? "stretch"
                : "center"
            }
            justifyContent="space-between"
            style={{
              minHeight: 74,
              gap: 12,
              padding:
                isMobile
                  ? 13
                  : "14px 18px",
              flexDirection:
                isMobile
                  ? "column"
                  : "row",
              borderBottom:
                "1px solid #EAECF0"
            }}
          >
            <Box>
              <Typography
                style={{
                  color: "#101828",
                  fontSize: 16,
                  fontWeight: 850
                }}
              >
                {folderLabel}
              </Typography>

              <Typography
                style={{
                  marginTop: 2,
                  color: "#98A2B3",
                  fontSize: 9.5
                }}
              >
                {visibleMessages.length ===
                1
                  ? "1 mensagem"
                  : `${visibleMessages.length} mensagens`}
                {mailboxFilter !==
                "all"
                  ? ` nesta caixa`
                  : ""}
              </Typography>
            </Box>


            <Box
              display="flex"
              alignItems="center"
              style={{
                gap: 7,
                width:
                  isMobile
                    ? "100%"
                    : "auto"
              }}
            >
              <TextField
                value={
                  search
                }
                onChange={
                  event =>
                    setSearch(
                      event.target.value
                    )
                }
                placeholder="Buscar e-mails"
                variant="outlined"
                size="small"
                style={{
                  width:
                    isMobile
                      ? "100%"
                      : 300,
                  maxWidth: "100%",
                  flexShrink: 0
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment
                      position="start"
                    >
                      <SearchIcon
                        style={{
                          color:
                            "#98A2B3",
                          fontSize: 19
                        }}
                      />
                    </InputAdornment>
                  )
                }}
              />

          {/* KORAX EMAIL FILTER TOOLBAR V1.5 */}
          <Box
            style={{
              marginTop: isMobile
                ? 10
                : 0,
              marginBottom: isMobile
                ? 10
                : 0,
              padding: 0,
              minWidth: 0
            }}
          >
            <Box
              display="flex"
              alignItems="center"
              style={{
                gap: 6,
                flexWrap:
                  isMobile
                    ? "wrap"
                    : "nowrap",
                minWidth: 0,
                whiteSpace: "nowrap"
              }}
            >
              <Button
                onClick={clearFilters}
                style={
                  quickFilterStyle(
                    !filtersActive
                  )
                }
              >
                Todos
              </Button>

              <Button
                onClick={
                  () =>
                    setUnreadOnly(
                      current =>
                        !current
                    )
                }
                disabled={
                  mailFolder !==
                    "inbox"
                }
                style={
                  quickFilterStyle(
                    unreadOnly
                  )
                }
              >
                Não lidos
              </Button>

              <Button
                onClick={
                  () =>
                    setFavoriteOnly(
                      current =>
                        !current
                    )
                }
                style={
                  quickFilterStyle(
                    favoriteOnly
                  )
                }
              >
                {favoriteOnly
                  ? "★ Favoritas"
                  : "☆ Favoritas"}
              </Button>

              <Button
                onClick={
                  () =>
                    setAttachmentOnly(
                      current =>
                        !current
                    )
                }
                style={
                  quickFilterStyle(
                    attachmentOnly
                  )
                }
              >
                Com anexo
              </Button>

              <Button
                onClick={
                  () =>
                    setDateFilter(
                      dateFilter ===
                        "today"
                        ? "all"
                        : "today"
                    )
                }
                style={
                  quickFilterStyle(
                    dateFilter ===
                      "today"
                  )
                }
              >
                Hoje
              </Button>

              <Button
                onClick={
                  () =>
                    setDateFilter(
                      dateFilter ===
                        "7"
                        ? "all"
                        : "7"
                    )
                }
                style={
                  quickFilterStyle(
                    dateFilter ===
                      "7"
                  )
                }
              >
                7 dias
              </Button>

              <Button
                onClick={
                  () =>
                    setDateFilter(
                      dateFilter ===
                        "30"
                        ? "all"
                        : "30"
                    )
                }
                style={
                  quickFilterStyle(
                    dateFilter ===
                      "30"
                  )
                }
              >
                30 dias
              </Button>

              <Box
                style={{
                  marginLeft:
                    isMobile
                      ? 0
                      : 4,
                  minHeight: 30,
                  padding: "0 10px",
                  border:
                    "1px solid #EAECF0",
                  borderRadius: 999,
                  background:
                    "#F8FAFC",
                  display: "flex",
                  alignItems: "center",
                  flexShrink: 0
                }}
              >
                <Typography
                  style={{
                    color: "#475467",
                    fontSize: 10.5,
                    fontWeight: 800,
                    whiteSpace: "nowrap"
                  }}
                >
                  {visibleMessages.length}
                  {" "}
                  {visibleMessages.length === 1
                    ? "resultado"
                    : "resultados"}
                </Typography>
              </Box>
            </Box>
          </Box>





              <Tooltip title="Atualizar">
                <IconButton
                  onClick={
                    onRefresh
                  }
                  disabled={
                    inboxLoading
                  }
                  style={{
                    width: 36,
                    height: 36,
                    border:
                      "1px solid #D0D5DD",
                    borderRadius: 10,
                    background:
                      "#FFFFFF",
                    flexShrink: 0
                  }}
                >
                  {inboxLoading ? (
                    <CircularProgress
                      size={16}
                    />
                  ) : (
                    <RefreshIcon
                      style={{
                        fontSize: 19,
                        color: "#475467"
                      }}
                    />
                  )}
                </IconButton>
              </Tooltip>
            </Box>
          </Box>


          {/* ESTADOS */}

          {loading ? (

            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              style={{
                minHeight: 500
              }}
            >
              <CircularProgress
                size={28}
              />
            </Box>

          ) : !ready ? (

            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              style={{
                minHeight: 520,
                padding: 30
              }}
            >
              <Box
                style={{
                  maxWidth: 430,
                  textAlign: "center"
                }}
              >
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  style={{
                    width: 58,
                    height: 58,
                    margin:
                      "0 auto 15px",
                    borderRadius: 16,
                    color: "#175CD3",
                    background: "#EEF4FF"
                  }}
                >
                  <MailOutlineIcon
                    style={{
                      fontSize: 27
                    }}
                  />
                </Box>

                <Typography
                  style={{
                    color: "#101828",
                    fontSize: 16,
                    fontWeight: 850
                  }}
                >
                  Nenhuma caixa disponível
                </Typography>

                <Typography
                  style={{
                    margin:
                      "7px auto 17px",
                    color: "#667085",
                    fontSize: 10.5,
                    lineHeight: 1.6
                  }}
                >
                  {user?.super
                    ? "Configure as caixas que esta empresa poderá utilizar."
                    : "Nenhuma caixa de e-mail está disponível para a sua conta neste momento."}
                </Typography>

                {user?.super && (
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={
                      onConfigure
                    }
                    style={{
                      height: 39,
                      borderRadius: 9,
                      textTransform:
                        "none",
                      boxShadow: "none",
                      fontWeight: 800
                    }}
                  >
                    Configurar caixas
                  </Button>
                )}
              </Box>
            </Box>

          ) : visibleMessages.length ===
            0 ? (

            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              style={{
                minHeight: 500,
                padding: 30
              }}
            >
              <Box
                style={{
                  maxWidth: 390,
                  textAlign: "center"
                }}
              >
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  style={{
                    width: 52,
                    height: 52,
                    margin:
                      "0 auto 13px",
                    borderRadius: 14,
                    background: "#F2F4F7",
                    color: "#667085"
                  }}
                >
                  <InboxIcon
                    style={{
                      fontSize: 24
                    }}
                  />
                </Box>

                <Typography
                  style={{
                    color: "#344054",
                    fontSize: 13,
                    fontWeight: 800
                  }}
                >
                  Nenhum e-mail encontrado
                </Typography>

                <Typography
                  style={{
                    marginTop: 5,
                    color: "#98A2B3",
                    fontSize: 10
                  }}
                >
                  {search ||
                  unreadOnly ||
                  mailboxFilter !==
                    "all"
                    ? "Tente limpar a busca ou alterar os filtros."
                    : mailFolder ===
                      "sent"
                      ? "Seus e-mails enviados aparecerão aqui."
                      : "Novas mensagens aparecerão aqui."}
                </Typography>
              </Box>
            </Box>

          ) : (

            /* LISTA */

            <Box
              style={{
                maxHeight:
                  isMobile
                    ? "none"
                    : "calc(100vh - 250px)",
                minHeight: 520,
                overflowY:
                  isMobile
                    ? "visible"
                    : "auto"
              }}
            >
              {visibleMessages.map(
                message => {

                  const isSent =
                    mailFolder ===
                    "sent";

                  const primary =
                    isSent
                      ? personName(
                          message?.to
                        ) ||
                        personAddress(
                          message?.to
                        ) ||
                        "Destinatário"
                      : personName(
                          message?.from
                        ) ||
                        personAddress(
                          message?.from
                        ) ||
                        "Remetente";

                  const secondary =
                    isSent
                      ? personAddress(
                          message?.to
                        )
                      : personAddress(
                          message?.from
                        );

                  const unread =
                    !isSent &&
                    Boolean(
                      message?.unseen
                    );

                  const favorite =
                    safeArray(
                      message?.flags
                    ).some(
                      flag =>
                        String(flag) ===
                        "\\Flagged"
                    );


                  const attachmentCount =
                    Number(
                      message
                        ?.attachmentCount ||
                      safeArray(
                        message
                          ?.attachments
                      ).length ||
                      0
                    );

                  return (
                    <Box
                      key={
                        message.id ||
                        `${message.mailboxId}-${message.uid}`
                      }
                      role="button"
                      tabIndex={0}
                      onClick={() =>
                        onOpenMessage(
                          message
                        )
                      }
                      onKeyDown={
                        event => {

                          if (
                            event.key ===
                              "Enter" ||
                            event.key ===
                              " "
                          ) {

                            event
                              .preventDefault();

                            onOpenMessage(
                              message
                            );
                          }
                        }
                      }
                      style={{
                        display:
                          isMobile
                            ? "block"
                            : "grid",
                        gridTemplateColumns:
                          "minmax(175px, .8fr) minmax(300px, 1.8fr) 110px",
                        gap:
                          isMobile
                            ? 7
                            : 16,
                        alignItems: "center",
                        minHeight: 76,
                        padding:
                          isMobile
                            ? "14px 15px"
                            : "10px 18px",
                        cursor: "pointer",
                        outline: "none",
                        borderBottom:
                          "1px solid #F2F4F7",
                        background:
                          unread
                            ? "#F8FAFF"
                            : "#FFFFFF"
                      }}
                    >

                      {/* REMETENTE */}

                      <Box
                        display="flex"
                        alignItems="center"
                        style={{
                          minWidth: 0,
                          gap: 10
                        }}
                      >
                        <Box
                          style={{
                            width: 7,
                            height: 7,
                            flexShrink: 0,
                            borderRadius:
                              "50%",
                            background:
                              unread
                                ? "#0057FF"
                                : "transparent"
                          }}
                        />

                        <Avatar
                          style={{
                            width: 35,
                            height: 35,
                            flexShrink: 0,
                            fontSize: 10,
                            fontWeight: 850,
                            background:
                              unread
                                ? "#E0EAFF"
                                : "#F2F4F7",
                            color:
                              unread
                                ? "#1849A9"
                                : "#475467"
                          }}
                        >
                          {initials(
                            primary
                          )}
                        </Avatar>

                        <Box
                          style={{
                            minWidth: 0
                          }}
                        >
                          <Typography
                            noWrap
                            style={{
                              color:
                                "#101828",
                              fontSize: 11.5,
                              fontWeight:
                                unread
                                  ? 850
                                  : 700
                            }}
                          >
                            {primary}
                          </Typography>

                          {secondary &&
                            secondary !==
                              primary && (
                              <Typography
                                noWrap
                                style={{
                                  marginTop: 2,
                                  color:
                                    "#98A2B3",
                                  fontSize: 8.5
                                }}
                              >
                                {secondary}
                              </Typography>
                            )}
                        </Box>
                      </Box>


                      {/* ASSUNTO */}

                      <Box
                        style={{
                          minWidth: 0
                        }}
                      >
                        <Box
                          display="flex"
                          alignItems="center"
                          style={{
                            minWidth: 0,
                            gap: 6
                          }}
                        >
                          <Typography
                            noWrap
                            style={{
                              minWidth: 0,
                              color:
                                "#344054",
                              fontSize: 11.5,
                              fontWeight:
                                unread
                                  ? 850
                                  : 700
                            }}
                          >
                            {message
                              ?.subject ||
                              "(sem assunto)"}
                          </Typography>

                          {attachmentCount >
                            0 && (
                            <AttachFileIcon
                              style={{
                                flexShrink: 0,
                                color:
                                  "#98A2B3",
                                fontSize: 15
                              }}
                            />
                          )}
                        </Box>

                        <Box
                          display="flex"
                          alignItems="center"
                          style={{
                            gap: 7,
                            marginTop: 5,
                            minWidth: 0
                          }}
                        >
                          <Typography
                            noWrap
                            style={{
                              maxWidth: 235,
                              padding:
                                "2px 7px",
                              borderRadius: 10,
                              background:
                                "#F2F4F7",
                              color:
                                "#667085",
                              fontSize: 8,
                              fontWeight: 700
                            }}
                          >
                            {message
                              ?.mailboxAddress ||
                              ""}
                          </Typography>

                          {attachmentCount >
                            0 && (
                            <Typography
                              style={{
                                color:
                                  "#98A2B3",
                                fontSize: 8
                              }}
                            >
                              {attachmentCount ===
                              1
                                ? "1 anexo"
                                : `${attachmentCount} anexos`}
                            </Typography>
                          )}
                        </Box>
                      </Box>


                      {/* DATA + FAVORITO */}

                      <Box
                        display="flex"
                        alignItems="center"
                        justifyContent={
                          isMobile
                            ? "flex-start"
                            : "flex-end"
                        }
                        style={{
                          gap: 6
                        }}
                      >
                        <Box
                          component="span"
                          role="button"
                          tabIndex={0}
                          title={
                            favorite
                              ? "Remover dos favoritos"
                              : "Marcar como favorito"
                          }
                          aria-label={
                            favorite
                              ? "Remover dos favoritos"
                              : "Marcar como favorito"
                          }
                          onClick={
                            event => {

                              event
                                .preventDefault();

                              event
                                .stopPropagation();

                              if (
                                onToggleFavorite
                              ) {
                                onToggleFavorite(
                                  message
                                );
                              }
                            }
                          }
                          onKeyDown={
                            event => {

                              if (
                                event.key ===
                                  "Enter" ||
                                event.key ===
                                  " "
                              ) {

                                event
                                  .preventDefault();

                                event
                                  .stopPropagation();

                                if (
                                  onToggleFavorite
                                ) {
                                  onToggleFavorite(
                                    message
                                  );
                                }
                              }
                            }
                          }
                          style={{
                            width: 25,
                            height: 25,
                            display:
                              "inline-flex",
                            alignItems:
                              "center",
                            justifyContent:
                              "center",
                            flexShrink: 0,
                            borderRadius: 7,
                            cursor:
                              "pointer",
                            userSelect:
                              "none",
                            color:
                              favorite
                                ? "#F79009"
                                : "#98A2B3",
                            fontSize: 19,
                            lineHeight: 1
                          }}
                        >
                          {favorite
                            ? "★"
                            : "☆"}
                        </Box>


                        <Typography
                          style={{
                            color:
                              unread
                                ? "#344054"
                                : "#98A2B3",
                            fontSize: 9.5,
                            fontWeight:
                              unread
                                ? 800
                                : 600
                          }}
                        >
                          {formatEmailDate(
                            message?.date
                          )}
                        </Typography>
                      </Box>
                    </Box>
                  );
                }
              )}
            </Box>
          )}
        </Box>
      </Paper>
    </Box>
  );
};


export default EmailWorkspaceV2;
